import React, { useEffect, useState } from "react";
import { Dropdown, Form, FormLabel } from "react-bootstrap";
import OverflowLabel from "./OverflowLabel";
import { ZCompactSearchBar } from "./ZCompactSearchBar";
import { ZIcon } from "./ZIcon";
import { ZIconType } from "helpers/utility/iconMap";
import { ZTooltip } from "./ZTooltip";
import { ZValidation } from "models/common/validation";
import classNames from "classnames";
import { v4 as uuidv4 } from 'uuid';
import { ZLabel } from "./ZLabel";

type SingleSelectDropdownProps<T> = {
    options: T[];
    value?: T;
    id?: string;
    label?: React.ReactNode;
    placeholder?: string;
    hasSearch?: boolean;
    searchPlaceholder?: string;
    canClear?: boolean;
    hasNoneOption?: boolean;
    noneOptionLabel?: string;
    dropdownIsDisabled?: boolean;
    barSize?: "compact" | "normal" | "fill";
    validation?: ZValidation;
    containerClassName?: string;
    containerStyle?: React.CSSProperties;
    minMenuHeightPx?: number;
    maxMenuHeightPx?: number;
    optionKey: (option: T) => any;
    display: (option: T) => string;
    disabled?: (option?: T) => boolean;
    onSelectionChange: (newOption?: T) => void;
    onBlur?: () => void;
    onAddNewOption?: (value: string) => Promise<void>;
    onDropdownClose?: () => void;
    onOpen?: () => void;
}

export function ZSingleSelectDropdown<T>({options, value, id, label, placeholder, hasSearch, searchPlaceholder, canClear, hasNoneOption, noneOptionLabel, dropdownIsDisabled, barSize, validation, containerClassName, containerStyle, minMenuHeightPx, maxMenuHeightPx, optionKey, display, disabled, onSelectionChange, onBlur, onAddNewOption, onDropdownClose, onOpen }: SingleSelectDropdownProps<T>) {
    const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
    const [searchText, setSearchText] = useState<string>("");
    const [hovering, setHovering] = useState<boolean>(false);
    const [shownOptions, setShownOptions] = useState<T[]>([]);
    const dropdownId = id ?? uuidv4();
    
    function filterBySearch(options: T[], search: string): T[] {
        return options.filter(opt => display(opt).containsKeyword(search));
    }

    function hasExactMatch(options: any[], search: string): boolean { 
        return options.findIndex(o => display(o).toLocaleLowerCase().match(new RegExp(`^${search.trim().toLocaleLowerCase()}$`, 'g')) != null) != -1;
    }

    useEffect(() => {
        if (isDropdownOpen) {
            // Focus search bar when dropdown is opened
            document.getElementById(`${dropdownId}-search-bar`)?.focus({preventScroll: true});
            if (value) {
                scrollToMyRef(dropdownId + optionKey(value).toString());
            }
        }
    }, [isDropdownOpen]);

    const scrollToMyRef = (id: string) => {
        var ref = document.getElementById(id);
        setTimeout(function () {
             ref?.scrollIntoView({
                 behavior: "smooth",
                 block: "nearest",
                 inline: 'nearest' 
             });
            }, 50);
    };

    useEffect(() => {
        setShownOptions(filterBySearch(options, hasSearch ? searchText : ""));
    }, [options, hasSearch, searchText]);

    return (
        <div className={containerClassName} style={{ ...containerStyle }}>{(label ? <ZLabel preventSelect>{label}</ZLabel> : <></>)}
        <Dropdown className='d-flex align-items-center' show={isDropdownOpen} onBlur={(e) => {
            // This line magically stops a click on an element inside the dropdown from closing the dropdown (ie this statement is true for child elements)
            if (e.currentTarget.contains(e.relatedTarget)) return;
            setIsDropdownOpen(false);
            onDropdownClose && onDropdownClose();
            onBlur && onBlur();            
        }} onClick={onOpen}>
            <Dropdown.Toggle 
                disabled={dropdownIsDisabled} 
                id="dropdown-basic" 
                variant='white' 
                className={classNames(
                    'd-flex', 
                    'w-100', 
                    'align-items-center', 
                    'rounded', 
                    'select-dropdown-toggle', 
                    {
                        [`select-dropdown-toggle-${barSize}`]: barSize != null,
                        [`invalid`]: validation?.invalid
                    }
                )}
                style={{ overflow: "hidden" }}
                onMouseEnter={() => setHovering(true)} onMouseLeave={() => setHovering(false)}
                onClick={(e) => { isDropdownOpen && onDropdownClose && onDropdownClose(); setIsDropdownOpen(!isDropdownOpen)}}>
                <OverflowLabel isVisible={true} labelClassName={`ps-1 select-dropdown-toggle-text ${value == null ? "select-dropdown-toggle-text-placeholder" : ""}`} tooltipStyle={{flexGrow: 1}} text={(hasNoneOption && value == null) ? noneOptionLabel ?? "None" : (value != null && display(value) || (placeholder != null ? placeholder : "Select option"))} />
                {value != null && canClear && hovering ? <span className="mdi mdi-close ms-auto select-dropdown-clear-button" onClick={(e) => {
                    e.stopPropagation();
                    onSelectionChange(undefined);
                }} /> : <></>}
                {validation?.invalid && <ZTooltip enabled text={validation?.error}><ZIcon variant="danger" icon={ZIconType.Error} /></ZTooltip>}
            </Dropdown.Toggle>

            <Dropdown.Menu tabIndex={0} style={{ overflow: "hidden", minHeight: minMenuHeightPx != null ? `${minMenuHeightPx}px` : undefined, maxHeight: `${maxMenuHeightPx ?? 300}px` }} className="select-dropdown-menu py-0" aria-required="true" onKeyDown={(e) => {
                if (e.key === "Escape") {
                    setIsDropdownOpen(false);
                }
            }}>
                <div className="d-flex flex-column" style={{ maxHeight: "inherit" }}> {/** This div is just to wrap the content so that we can get correct overflow on the list of items */}
                    {hasSearch ? <div className="bg-white dropdown-search-bar"><ZCompactSearchBar id={`${dropdownId}-search-bar`} className="mb-0" placeholderText={searchPlaceholder} searchText={searchText} onChange={(t) => setSearchText(t)} /></div>: <></> }
                    { onAddNewOption != null && searchText.trim().length > 0 && !hasExactMatch(options, searchText) ? 
                        <span
                            className={`d-flex dropdown-item ps-2 align-items-center select-dropdown-item mt-1`}
                            key={"add-new-opt"}
                            tabIndex={0}
                            id={dropdownId + "-add-new-opt"}
                            draggable={false}
                            onClick={(x) => {
                                onAddNewOption(searchText.trim());
                                setIsDropdownOpen(false);
                                x.stopPropagation();
                                x.preventDefault();
                            }}
                            onKeyDown={(x) => {
                                if (x.key === "Enter" || x.key === " ") {
                                    onAddNewOption(searchText.trim());
                                    setIsDropdownOpen(false);
                                    x.stopPropagation();
                                    x.preventDefault();
                                }
                            }}
                    >
                        <ZIcon icon={ZIconType.Add} iconClassName="me-1"/>
                        <OverflowLabel labelClassName="prevent-select" isVisible={isDropdownOpen} text={`Add \"${searchText.trim()}\"`} /></span> :
                        <></>
                    }
                    {hasSearch && <hr className="border-1 m-1" />}
                    <div style={{overflowY: "auto"}}>
                        {hasNoneOption ?
                        <span
                            tabIndex={0}
                            className={`d-flex dropdown-item ps-2 prevent-select align-items-center select-dropdown-item ${disabled && disabled(undefined) ? "disabled" : ""}`}
                            onClick={(x) => {
                                setIsDropdownOpen(false);
                                onSelectionChange(undefined);
                            }}
                            onKeyDown={(x) => {
                                if (x.key === "Enter" || x.key === " ") {
                                    setIsDropdownOpen(false);
                                    onSelectionChange(undefined);
                                    x.stopPropagation();
                                    x.preventDefault();
                                }
                            }}
                        >
                            <Form.Check
                                type="radio"
                                className="vertical-align-middle pe-2"
                                id="default-checkbox"
                                key={dropdownId + "-none"}
                                tabIndex={-1}
                                value={undefined}
                                disabled={disabled && disabled(undefined)}
                                checked={value == null}
                                onChange={() => setIsDropdownOpen(false)}
                            />{noneOptionLabel != null ? noneOptionLabel : "None"}</span> : <></>}
                    {shownOptions.length === 0 && !hasNoneOption ? 
                        <span className="d-flex px-2 py-1 align-items-center prevent-select">Nothing here...</span> 
                    : shownOptions.map((e: T, i: number) => (
                        <Dropdown.Item
                            draggable={false}
                            tabIndex={disabled && disabled(e) ? -1 : 0}
                            className={`d-flex dropdown-item ps-2 prevent-select align-items-center select-dropdown-item ${disabled && disabled(e) ? "disabled" : ""}`}
                            key={dropdownId + optionKey(e)}
                            id={dropdownId + optionKey(e).toString()}
                            onClick={(x) => {
                                setIsDropdownOpen(false);
                                onSelectionChange(e);
                            }}
                            onKeyDown={(x) => {
                                if (x.key === "Enter" || x.key === " ") {
                                    setIsDropdownOpen(false);
                                    onSelectionChange(e);
                                    x.stopPropagation();
                                    x.preventDefault();
                                }
                            }}
                        >
                            <Form.Check
                                type="radio"
                                className="vertical-align-middle pe-2"
                                id="default-checkbox"
                                tabIndex={-1}
                                key={dropdownId + optionKey(e)}
                                value={optionKey(e).toString()}
                                disabled={disabled && disabled(e)}
                                checked={value != null && (optionKey(value) == optionKey(e))}
                                onChange={() => {
                                    setIsDropdownOpen(false);
                                }}
                            /><OverflowLabel isVisible={isDropdownOpen} text={display(e)} /></Dropdown.Item>
                    ))}</div>
                </div>
            </Dropdown.Menu>
        </Dropdown></div>
    );
}